import React from "react";
import { Dialog, DialogContent, DialogTitle } from "../../ui/dialog";
import { RadioGroup, RadioGroupItem } from "../../ui/radio-group";
import { Label } from "../../ui/label";
import { Button } from "../../ui/button";
import { Loader2, CheckCircle2 } from "lucide-react";
import { ReactComponent as GoogleDriveIcon } from "../../../static/icons/google-drive.svg";
import {
    AccessMode,
    accessModeDescriptions,
    StorageLevel,
    storageLevelDescriptions,
} from "../../../lib/google-drive-constants";

interface GoogleDriveConnectDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    storageLevel: StorageLevel | null;
    onStorageLevelChange: (level: StorageLevel) => void;
    accessMode: AccessMode | null;
    onAccessModeChange: (mode: AccessMode) => void;
    onConnect: () => void;
    connecting: boolean;
    isPending: boolean;
    success: boolean;
    canConnect: boolean;
}

export function GoogleDriveConnectDialog(props: GoogleDriveConnectDialogProps) {
    return (
        <Dialog open={props.open} onOpenChange={props.onOpenChange}>
            <DialogContent className="sm:max-w-md">
                <DialogTitle className="sr-only">Connect Google Drive</DialogTitle>
                {props.success ? (
                    <div className="flex flex-col items-center gap-4 py-4 text-center">
                        <CheckCircle2 className="h-10 w-10 text-green-500" />
                        <h3 className="text-lg font-semibold">Google Drive connected!</h3>
                        <p className="text-sm text-muted-foreground">
                            Sync started! You can follow the progress on the settings page or in the top menu bar.
                        </p>
                        <Button onClick={() => props.onOpenChange(false)}>Got it</Button>
                    </div>
                ) : (
                    <div className="flex flex-col gap-5">
                        <div className="flex items-center gap-3">
                            <GoogleDriveIcon className="h-6 w-6" />
                            <h3 className="text-lg font-semibold">Connect Google Drive</h3>
                        </div>

                        <div className="flex flex-col gap-2">
                            <p className="text-sm font-medium">Access mode</p>
                            <p className="text-xs text-muted-foreground">
                                Choose how much access Tekkr gets to your Google Drive.
                            </p>
                            <RadioGroup
                                value={props.accessMode ?? ""}
                                onValueChange={(v) => props.onAccessModeChange(v as AccessMode)}
                                className="flex flex-col gap-2"
                            >
                                {(Object.keys(accessModeDescriptions) as AccessMode[]).map((mode) => (
                                    <div key={mode} className="flex items-start space-x-3">
                                        <RadioGroupItem value={mode} id={`dialog-access-${mode}`} className="mt-0.5" />
                                        <Label
                                            htmlFor={`dialog-access-${mode}`}
                                            className="flex flex-col gap-0.5 font-normal"
                                        >
                                            <span className="font-medium">{accessModeDescriptions[mode].label}</span>
                                            <span className="text-xs text-muted-foreground">
                                                {accessModeDescriptions[mode].description}
                                            </span>
                                        </Label>
                                    </div>
                                ))}
                            </RadioGroup>
                        </div>

                        <div className="flex flex-col gap-2">
                            <p className="text-sm font-medium">Storage level</p>
                            <p className="text-xs text-muted-foreground">
                                Choose how much document content Tekkr should store for AI-powered retrieval.
                            </p>
                            <RadioGroup
                                value={props.storageLevel ?? ""}
                                onValueChange={(v) => props.onStorageLevelChange(v as StorageLevel)}
                                className="flex flex-col gap-2"
                            >
                                {(Object.keys(storageLevelDescriptions) as StorageLevel[]).map((level) => (
                                    <div key={level} className="flex items-start space-x-3">
                                        <RadioGroupItem
                                            value={level}
                                            id={`dialog-storage-${level}`}
                                            className="mt-0.5"
                                        />
                                        <Label
                                            htmlFor={`dialog-storage-${level}`}
                                            className="flex flex-col gap-0.5 font-normal"
                                        >
                                            <span className="font-medium">{storageLevelDescriptions[level].label}</span>
                                            <span className="text-xs text-muted-foreground">
                                                {storageLevelDescriptions[level].description}
                                            </span>
                                        </Label>
                                    </div>
                                ))}
                            </RadioGroup>
                        </div>

                        <Button
                            onClick={props.onConnect}
                            disabled={!props.canConnect || props.connecting || props.isPending}
                            className="w-full"
                        >
                            {(props.connecting || props.isPending) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Connect now
                        </Button>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
